import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../auth/AuthProvider';
import * as api from '../api/client';

interface AppUser {
  id: string;
  fullName: string;
  email: string;
  mercuryTerritory: string;
  appTerritory: string;
  isActive: boolean;
  role: string;
  teamId: string | null;
  monthlyTarget: number;
}

interface Team {
  id: string;
  name: string;
  territory: string;
  teamLeadId: string | null;
  teamLeadName: string | null;
  members: AppUser[];
}

interface TerritoryInfo {
  name: string;
  directorId: string | null;
  directorName: string | null;
  userCount: number;
  teamCount: number;
  unassignedCount: number;
}

interface CloseReason {
  value: number;
  label: string;
  visible: boolean;
}

const TERRITORIES = ['Bristol', 'London', 'London Contract', 'Chicago', 'Chicago Contract', 'New York', 'Unassigned'];

type SettingsSection = 'teams' | 'close-reasons' | 'notifications' | 'duplicates' | 'activity';

export function SettingsView() {
  const auth = useAuth(); // keep for potential future use
  const [activeTab, setActiveTab] = useState<string>('Bristol');
  // Open a specific section from a link, e.g. the duplicate email → ?section=duplicates
  const initialSection = (new URLSearchParams(window.location.search).get('section') || 'teams') as SettingsSection;
  const [activeSection, setActiveSection] = useState<SettingsSection>(initialSection);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [allUsers, setAllUsers] = useState<AppUser[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [territories, setTerritories] = useState<TerritoryInfo[]>([]);
  const [closeReasons, setCloseReasons] = useState<CloseReason[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [newTeamName, setNewTeamName] = useState('');
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [usersRes, allUsersRes, teamsRes, terrRes, reasonsRes] = await Promise.all([
        api.getSettingsUsers(activeTab),
        api.getSettingsUsers(), // all users for director picker
        api.getSettingsTeams(activeTab),
        api.getSettingsTerritories(),
        api.getCloseReasons(),
      ]);
      setUsers(usersRes.users || []);
      setAllUsers(allUsersRes.users || []);
      setTeams(teamsRes.teams || []);
      setTerritories(terrRes.territories || []);
      setCloseReasons(reasonsRes.reasons || []);
      setError('');
    } catch (err: any) {
      setError(err.message);
    }
  }, [activeTab]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncMessage('');
    try {
      const result = await api.syncUsers();
      setSyncMessage(result.message);
      await loadData();
    } catch (err: any) {
      setSyncMessage(`Sync failed: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) return;
    try {
      await api.createSettingsTeam({ name: newTeamName.trim(), territory: activeTab });
      setNewTeamName('');
      await loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleAssignToTeam = async (userId: string, teamId: string | null) => {
    try {
      await api.updateSettingsUser(userId, { teamId });
      await loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleSetRole = async (userId: string, role: string) => {
    try {
      await api.updateSettingsUser(userId, { role });
      await loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const [savedTargetId, setSavedTargetId] = useState<string | null>(null);

  const handleSetTarget = async (userId: string, target: number) => {
    try {
      await api.updateSettingsUser(userId, { monthlyTarget: target });
      setSavedTargetId(userId);
      setTimeout(() => setSavedTargetId(null), 2000);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleSetTeamLead = async (teamId: string, userId: string, userName: string) => {
    try {
      await api.updateSettingsTeam(teamId, { teamLeadId: userId, teamLeadName: userName });
      await loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleSetDirector = async (territory: string, userId: string, userName: string) => {
    try {
      await api.updateSettingsTerritory(territory, { directorId: userId, directorName: userName });
      await loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleToggleCloseReason = async (value: number, visible: boolean) => {
    try {
      await api.updateCloseReason(value, visible);
      setCloseReasons(prev => prev.map(r => r.value === value ? { ...r, visible } : r));
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteTeam = async (teamId: string) => {
    try {
      await api.deleteSettingsTeam(teamId);
      await loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const unassignedUsers = users.filter(u => !u.teamId && u.isActive);
  const currentTerritory = territories.find(t => t.name === activeTab);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '16px', fontWeight: 500 }}>Settings</h1>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {syncMessage && (
            <span style={{ fontSize: '12px', color: 'var(--accent-teal)' }}>{syncMessage}</span>
          )}
          <button className="btn btn-primary" onClick={handleSync} disabled={syncing}>
            {syncing ? 'Syncing...' : 'Sync from Mercury'}
          </button>
        </div>
      </div>

      {error && (
        <div className="warning-banner" style={{ marginBottom: '16px' }}>{error}</div>
      )}

      {/* Section tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px' }}>
        <button
          className={`month-tab${activeSection === 'teams' ? ' active' : ''}`}
          onClick={() => setActiveSection('teams')}
        >
          Teams & Users
        </button>
        <button
          className={`month-tab${activeSection === 'close-reasons' ? ' active' : ''}`}
          onClick={() => setActiveSection('close-reasons')}
        >
          Close Reasons
        </button>
        <button
          className={`month-tab${activeSection === 'notifications' ? ' active' : ''}`}
          onClick={() => setActiveSection('notifications')}
        >
          Notifications
        </button>
        <button
          className={`month-tab${activeSection === 'duplicates' ? ' active' : ''}`}
          onClick={() => setActiveSection('duplicates')}
        >
          Duplicates
        </button>
        <button
          className={`month-tab${activeSection === 'activity' ? ' active' : ''}`}
          onClick={() => setActiveSection('activity')}
        >
          Activity Log
        </button>
      </div>

      {activeSection === 'activity' ? (
        /* ── Activity Log Section ── */
        <ActivityLogSection />
      ) : activeSection === 'duplicates' ? (
        /* ── Duplicate Candidates Section ── */
        <DuplicatesSection />
      ) : activeSection === 'notifications' ? (
        /* ── Notifications Section ── */
        <NotificationsSection />
      ) : activeSection === 'close-reasons' ? (
        /* ── Close Reasons Section ── */
        <div className="card">
          <div className="card-header" style={{ marginBottom: '16px' }}>Vacancy Close Reasons</div>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
            Toggle which close reasons are available to consultants when closing vacancies.
          </p>
          {closeReasons.map(reason => (
            <div
              key={reason.value}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 0',
                borderBottom: '1px solid var(--border-subtle)',
              }}
            >
              <span style={{ fontSize: '13px', color: reason.visible ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                {reason.label}
              </span>
              <button
                className={`btn ${reason.visible ? 'btn-primary' : 'btn-secondary'}`}
                style={{ padding: '4px 12px', fontSize: '11px' }}
                onClick={() => handleToggleCloseReason(reason.value, !reason.visible)}
              >
                {reason.visible ? 'Visible' : 'Hidden'}
              </button>
            </div>
          ))}
          {closeReasons.length === 0 && (
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
              Sync users first to initialise close reasons.
            </p>
          )}
        </div>
      ) : (
        /* ── Teams & Users Section ── */
        <>
          {/* Territory tabs */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '20px' }}>
            {TERRITORIES.map(t => {
              const info = territories.find(tr => tr.name === t);
              return (
                <button
                  key={t}
                  className={`month-tab${activeTab === t ? ' active' : ''}`}
                  onClick={() => setActiveTab(t)}
                >
                  {t}
                  {info && (
                    <span style={{ marginLeft: '6px', opacity: 0.6 }}>{info.userCount}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Director */}
          <div className="card" style={{ marginBottom: '16px' }}>
            <div className="card-header">Director — {activeTab}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
              <span style={{ fontSize: '14px', fontWeight: 500 }}>
                {currentTerritory?.directorName || 'Not assigned'}
              </span>
              <select
                style={{
                  background: 'var(--bg-page)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-card)',
                  borderRadius: '6px',
                  padding: '4px 8px',
                  fontSize: '12px',
                }}
                value={currentTerritory?.directorId || ''}
                onChange={e => {
                  const user = allUsers.find(u => u.id === e.target.value);
                  if (user) handleSetDirector(activeTab, user.id, user.fullName);
                }}
              >
                <option value="">Select director...</option>
                {allUsers.filter(u => u.isActive).map(u => (
                  <option key={u.id} value={u.id}>{u.fullName}{u.appTerritory ? ` (${u.appTerritory})` : ''}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Teams */}
          {teams.map(team => (
            <div key={team.id} className="card" style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div>
                  <span style={{ fontSize: '14px', fontWeight: 500 }}>{team.name}</span>
                  {team.teamLeadName && (
                    <span className="pill pill-teal" style={{ marginLeft: '8px' }}>
                      Lead: {team.teamLeadName}
                    </span>
                  )}
                </div>
                <button
                  className="btn btn-secondary"
                  style={{ padding: '4px 8px', fontSize: '11px' }}
                  onClick={() => handleDeleteTeam(team.id)}
                >
                  Delete
                </button>
              </div>

              {/* Team lead selector */}
              <div style={{ marginBottom: '12px' }}>
                <select
                  style={{
                    background: 'var(--bg-page)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-card)',
                    borderRadius: '6px',
                    padding: '4px 8px',
                    fontSize: '12px',
                  }}
                  value={team.teamLeadId || ''}
                  onChange={e => {
                    const user = users.find(u => u.id === e.target.value);
                    if (user) handleSetTeamLead(team.id, user.id, user.fullName);
                  }}
                >
                  <option value="">Set team lead...</option>
                  {team.members.map(m => (
                    <option key={m.id} value={m.id}>{m.fullName}</option>
                  ))}
                </select>
              </div>

              {/* Members table */}
              {team.members.length > 0 ? (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Target</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {team.members.map(m => (
                      <tr key={m.id}>
                        <td>
                          {m.fullName}
                          {m.id === team.teamLeadId && (
                            <span className="pill pill-teal" style={{ marginLeft: '6px' }}>lead</span>
                          )}
                        </td>
                        <td style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>{m.role}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <input
                              id={`target-${m.id}`}
                              type="number"
                              defaultValue={m.monthlyTarget}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  handleSetTarget(m.id, parseInt((e.target as HTMLInputElement).value) ?? 50);
                                }
                              }}
                              style={{
                                width: '60px',
                                background: 'var(--bg-page)',
                                color: 'var(--text-primary)',
                                border: '1px solid var(--border-card)',
                                borderRadius: '4px',
                                padding: '2px 6px',
                                fontSize: '12px',
                              }}
                            />
                            <button
                              className="btn btn-primary"
                              style={{ padding: '2px 6px', fontSize: '10px' }}
                              onClick={() => {
                                const input = document.getElementById(`target-${m.id}`) as HTMLInputElement;
                                handleSetTarget(m.id, parseInt(input?.value ?? '50'));
                              }}
                            >
                              {savedTargetId === m.id ? '✓' : 'Save'}
                            </button>
                          </div>
                        </td>
                        <td>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: '2px 8px', fontSize: '11px' }}
                            onClick={() => handleAssignToTeam(m.id, null)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>No members yet</p>
              )}
            </div>
          ))}

          {/* Add team */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
            <input
              type="text"
              placeholder="New team name..."
              value={newTeamName}
              onChange={e => setNewTeamName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreateTeam()}
              style={{
                flex: 1,
                maxWidth: '240px',
                background: 'var(--bg-card)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-card)',
                borderRadius: '8px',
                padding: '8px 12px',
                fontSize: '13px',
              }}
            />
            <button className="btn btn-primary" onClick={handleCreateTeam}>
              Add Team
            </button>
          </div>

          {/* Unassigned users */}
          {unassignedUsers.length > 0 && (
            <div className="card" style={{ borderColor: 'rgba(116, 92, 80, 0.3)' }}>
              <div className="card-header" style={{ color: 'var(--state-warning)' }}>
                Unassigned — {activeTab} ({unassignedUsers.length})
              </div>
              <table className="data-table" style={{ marginTop: '8px' }}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Target</th>
                    <th>Assign to</th>
                  </tr>
                </thead>
                <tbody>
                  {unassignedUsers.map(u => (
                    <tr key={u.id}>
                      <td>{u.fullName}</td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>{u.email}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <input
                            id={`target-${u.id}`}
                            type="number"
                            defaultValue={u.monthlyTarget}
                            onKeyDown={e => {
                              if (e.key === 'Enter') {
                                handleSetTarget(u.id, parseInt((e.target as HTMLInputElement).value) ?? 50);
                              }
                            }}
                            style={{
                              width: '60px',
                              background: 'var(--bg-page)',
                              color: 'var(--text-primary)',
                              border: '1px solid var(--border-card)',
                              borderRadius: '4px',
                              padding: '2px 6px',
                              fontSize: '12px',
                            }}
                          />
                          <button
                            className="btn btn-primary"
                            style={{ padding: '2px 6px', fontSize: '10px' }}
                            onClick={() => {
                              const input = document.getElementById(`target-${u.id}`) as HTMLInputElement;
                              handleSetTarget(u.id, parseInt(input?.value ?? '50'));
                            }}
                          >
                            {savedTargetId === u.id ? '✓' : 'Save'}
                          </button>
                        </div>
                      </td>
                      <td>
                        <select
                          style={{
                            background: 'var(--bg-page)',
                            color: 'var(--text-primary)',
                            border: '1px solid var(--border-card)',
                            borderRadius: '6px',
                            padding: '2px 6px',
                            fontSize: '12px',
                          }}
                          value=""
                          onChange={e => handleAssignToTeam(u.id, e.target.value)}
                        >
                          <option value="">Select team...</option>
                          {teams.map(t => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {users.length === 0 && (
            <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '12px' }}>
                No users synced yet
              </p>
              <button className="btn btn-primary" onClick={handleSync} disabled={syncing}>
                {syncing ? 'Syncing...' : 'Sync from Mercury'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Notifications Section ──

// ── Duplicate Candidates Section ──

interface DuplicateRow {
  id: string;
  reportedAt: string;
  originalId: string;
  originalName: string;
  originalEmail: string;
  duplicateId: string;
  duplicateName: string;
  consultant: string;
  fileName: string;
  matchedBy?: string; // 'name' or 'email <address>'
  label: string;
}

const MERCURY_BASE = 'https://saragossa.crm11.dynamics.com';
const contactLink = (id: string) =>
  `${MERCURY_BASE}/main.aspx?pagetype=entityrecord&etn=contact&id=${id}`;

/** Copies a candidate's name to the clipboard — for pasting into Mercury's search. */
function CopyName({ name }: { name: string }) {
  const [copied, setCopied] = useState(false);
  if (!name) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(name);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — nothing useful to do */ }
  };
  return (
    <button
      type="button"
      className="btn btn-secondary"
      onClick={copy}
      title="Copy name"
      style={{ padding: '1px 7px', fontSize: '11px', marginLeft: '6px', verticalAlign: 'middle', minWidth: '52px' }}
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function DuplicatesSection() {
  const [rows, setRows] = useState<DuplicateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getCvDuplicates();
      setRows(res.duplicates || []);
      setError('');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markActioned = async (row: DuplicateRow) => {
    setBusyId(row.id);
    try {
      await api.resolveCvDuplicate(row.id, true);
      setRows(prev => prev.filter(r => r.id !== row.id)); // optimistic removal
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: '16px' }}>
        <div className="card-header" style={{ marginBottom: '8px' }}>Suspected duplicate candidates</div>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0 }}>
          A CV was uploaded but the candidate still shows no CV, while a similarly-named contact has one —
          DaXtra most likely filed it against a duplicate. Open both records in Mercury, merge them, then tick it off.
        </p>
      </div>

      {error && <div className="warning-banner" style={{ marginBottom: '16px' }}>{error}</div>}

      {loading ? (
        <div className="card" style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)' }}>
          Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)' }}>
          No open duplicates — all clear. 🎉
        </div>
      ) : (
        <div className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Original (no CV)</th>
                <th>Likely duplicate (has CV)</th>
                <th>Uploaded by</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>
                      {r.originalId
                        ? <a href={contactLink(r.originalId)} target="_blank" rel="noreferrer">{r.originalName || 'Unknown'}</a>
                        : (r.originalName || 'Unknown')}
                      <CopyName name={r.originalName} />
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{r.originalEmail || 'no email on record'}</div>
                  </td>
                  <td>
                    {r.duplicateId
                      ? <a href={contactLink(r.duplicateId)} target="_blank" rel="noreferrer">{r.duplicateName || 'View'}</a>
                      : (r.duplicateName || '—')}
                    {r.matchedBy && (
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                        {r.matchedBy === 'name' ? 'matched on name' : `matched on ${r.matchedBy}`}
                      </div>
                    )}
                  </td>
                  <td style={{ fontSize: '13px' }}>
                    {r.consultant || '—'}
                    {r.fileName && <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{r.fileName}</div>}
                  </td>
                  <td>
                    <button
                      className="btn btn-primary"
                      style={{ padding: '4px 12px', fontSize: '12px' }}
                      disabled={busyId === r.id}
                      onClick={() => markActioned(r)}
                    >
                      {busyId === r.id ? '…' : '✓ Actioned'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function NotificationsSection() {
  const { getGraphToken } = useAuth();
  const [threshold, setThreshold] = useState(50);
  const [includeUnassigned, setIncludeUnassigned] = useState(false);
  // Tracks WHICH action is running, so only the clicked button shows a spinner
  // (a shared boolean made every button look like it was sending).
  const [active, setActive] = useState<string>('');
  const loading = !!active;
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  const handleScoreAlerts = async (dryRun: boolean) => {
    if (!dryRun && !window.confirm(`This emails EVERY consultant below ${threshold}%, plus their team leads and directors. Continue?`)) return;
    setActive(dryRun ? 'alerts-preview' : 'alerts-send');
    setError('');
    setResults(null);
    try {
      const graphToken = await getGraphToken();
      const result = await api.sendScoreAlerts(
        { threshold: threshold / 100, includeUnassigned, dryRun },
        graphToken
      );
      setResults({ type: 'alerts', ...result });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActive('');
    }
  };

  const handleSummary = async () => {
    setActive('summary');
    setError('');
    setResults(null);
    try {
      const graphToken = await getGraphToken();
      const result = await api.sendSummary(graphToken);
      setResults({ type: 'summary', ...result });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActive('');
    }
  };


  return (
    <div>
      {/* Settings */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div className="card-header" style={{ marginBottom: '16px' }}>Notification Settings</div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <label style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Score alert threshold</label>
          <input
            type="number"
            value={threshold}
            onChange={e => setThreshold(parseInt(e.target.value) ?? 50)}
            style={{
              width: '60px',
              background: 'var(--bg-page)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-card)',
              borderRadius: '4px',
              padding: '4px 8px',
              fontSize: '13px',
            }}
          />
          <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>%</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <input
            type="checkbox"
            id="includeUnassigned"
            checked={includeUnassigned}
            onChange={e => setIncludeUnassigned(e.target.checked)}
            style={{ accentColor: 'var(--accent-teal)' }}
          />
          <label htmlFor="includeUnassigned" style={{ fontSize: '13px', color: 'var(--text-secondary)', cursor: 'pointer' }}>
            Include unassigned consultants (no team)
          </label>
        </div>
      </div>

      {/* Score Alerts */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div className="card-header" style={{ marginBottom: '8px' }}>Score Alerts</div>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
          Emails consultants whose score is below {threshold}%. Sent from your mailbox via Graph.
        </p>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-secondary" onClick={() => handleScoreAlerts(true)} disabled={loading}>
            {active === 'alerts-preview' ? '...' : 'Preview (dry run)'}
          </button>
          <button className="btn btn-primary" onClick={() => handleScoreAlerts(false)} disabled={loading}>
            {active === 'alerts-send' ? 'Sending...' : 'Send alerts'}
          </button>
        </div>
      </div>

      {/* Summary email (admin only) */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div className="card-header" style={{ marginBottom: '8px' }}>Summary email</div>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
          Emails you a traffic-light breakdown of everyone — 🔴 0–25%, 🟠 25–50%, 🟢 50%+ — plus territory averages.
          Only sent to you; nobody else is emailed.
        </p>
        <button className="btn btn-primary" onClick={handleSummary} disabled={loading}>
          {active === 'summary' ? 'Sending...' : 'Email me the summary'}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="warning-banner" style={{ marginBottom: '16px' }}>{error}</div>
      )}

      {/* Results */}
      {results && (
        <div className="card">
          <div className="card-header" style={{ marginBottom: '12px' }}>
            {results.dryRun ? 'Preview — no emails sent' : 'Sent'}
          </div>
          {results.type === 'alerts' && (
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
              Checked {results.checked} consultants. {results.belowThreshold} below {Math.round((results.threshold || 0.5) * 100)}% threshold.
            </p>
          )}
          {results.type === 'reminders' && (
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
              {results.totalEligible} consultants eligible.
            </p>
          )}
          {results.type === 'summary' && (
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
              {results.sent ? `Summary emailed to ${results.to}.` : 'Summary could not be sent.'}{' '}
              {results.scored} scored — {results.bands?.red ?? 0} red, {results.bands?.amber ?? 0} amber, {results.bands?.green ?? 0} green.
            </p>
          )}
          {(results.results || []).length > 0 && (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  {results.type === 'alerts' && <th>Score</th>}
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {(results.results || []).map((r: any, i: number) => (
                  <tr key={i}>
                    <td>{r.name}</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>{r.email}</td>
                    {results.type === 'alerts' && (
                      <td style={{ color: 'var(--state-warning)', fontWeight: 500 }}>
                        {Math.round((r.score || 0) * 100)}%
                      </td>
                    )}
                    <td>
                      {r.error ? (
                        <span className="pill pill-warning">{r.error}</span>
                      ) : r.sent ? (
                        <span className="pill pill-sage">Sent</span>
                      ) : (
                        <span className="pill pill-teal">{results.dryRun ? 'Would send' : 'Queued'}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {(results.results || []).length === 0 && (
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
              {results.type === 'alerts' ? 'No consultants below threshold.' : 'No eligible consultants.'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Activity Log Section ──

const ACTION_DISPLAY: Record<string, { label: string; color: string }> = {
  'log-call': { label: 'Log call', color: 'var(--accent-teal)' },
  'close-vacancy': { label: 'Close vacancy', color: 'var(--state-warning)' },
  'fix-currency': { label: 'Fix currency', color: 'var(--accent-teal)' },
  'fix-address': { label: 'Fix address', color: 'var(--accent-teal)' },
  'fix-solution': { label: 'Fix solution', color: 'var(--accent-teal)' },
  'add-tags': { label: 'Add tags', color: 'var(--accent-teal)' },
  'edit-contact': { label: 'Edit contact', color: 'var(--accent-teal)' },
  'upload-cv': { label: 'Upload CV', color: 'var(--state-positive)' },
  'score-alert': { label: 'Score alert', color: '#745C50' },
  'team-alert': { label: 'Team alert', color: '#745C50' },
  'territory-alert': { label: 'Territory alert', color: '#745C50' },
  'monthly-reminder': { label: 'Monthly reminder', color: '#745C50' },
};

function ActivityLogSection() {
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await api.getAuditLog(days);
      setEntries(result.entries || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  // Chart data — issues fixed per user (excludes notification sends)
  const NOTIFICATION_ACTIONS = new Set(['score-alert', 'team-alert', 'territory-alert', 'monthly-reminder']);
  const fixEntries = entries.filter(e => !NOTIFICATION_ACTIONS.has(e.action));
  const byUser = fixEntries.reduce<Record<string, number>>((acc, e) => {
    const name = e.consultantName || 'Unknown';
    acc[name] = (acc[name] || 0) + 1;
    return acc;
  }, {});
  const chartData = Object.entries(byUser).sort((a, b) => b[1] - a[1]);
  const chartMax = chartData.length > 0 ? chartData[0][1] : 1;

  // Group by date for readability
  const grouped = entries.reduce<Record<string, any[]>>((acc, e) => {
    const date = e.timestamp ? new Date(e.timestamp).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : 'Unknown';
    (acc[date] = acc[date] || []).push(e);
    return acc;
  }, {});

  return (
    <div>
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="card-header" style={{ marginBottom: 0 }}>Fixed Issues — Last {days} days</div>
          <div style={{ display: 'flex', gap: '6px' }}>
            {[7, 30, 60].map(d => (
              <button
                key={d}
                className={`btn ${days === d ? 'btn-primary' : 'btn-secondary'}`}
                style={{ padding: '4px 10px', fontSize: '11px' }}
                onClick={() => setDays(d)}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '8px' }}>
          {entries.length} action{entries.length !== 1 ? 's' : ''} recorded
        </p>
      </div>

      {error && <div className="warning-banner" style={{ marginBottom: '16px' }}>{error}</div>}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>Loading...</div>
      ) : entries.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: 'var(--text-secondary)' }}>No activity recorded yet. Actions taken by consultants will appear here.</p>
        </div>
      ) : (
        <>
          {/* ── Issues by user chart ── */}
          {chartData.length > 0 && (
            <div className="card" style={{ marginBottom: '16px' }}>
              <div className="card-header" style={{ marginBottom: '16px' }}>Issues Fixed by User</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {chartData.map(([name, count]) => (
                  <div key={name} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '140px', fontSize: '13px', fontWeight: 500, textAlign: 'right', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {name}
                    </div>
                    <div style={{ flex: 1, height: '28px', background: 'var(--bg-page)', borderRadius: '6px', overflow: 'hidden' }}>
                      <div
                        style={{
                          width: `${Math.max((count / chartMax) * 100, 2)}%`,
                          height: '100%',
                          background: 'var(--accent-teal)',
                          borderRadius: '6px',
                          transition: 'width 0.4s ease',
                        }}
                      />
                    </div>
                    <div style={{ width: '36px', fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', flexShrink: 0 }}>
                      {count}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Date-grouped list ── */}
          {Object.entries(grouped).map(([date, items]) => (
          <div key={date} style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
              {date}
            </div>
            <div className="card" style={{ padding: 0 }}>
              <table className="data-table">
                <tbody>
                  {items.map((e: any, i: number) => {
                    const display = ACTION_DISPLAY[e.action] || { label: e.action, color: 'var(--text-secondary)' };
                    const time = e.timestamp ? new Date(e.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
                    return (
                      <tr key={i}>
                        <td style={{ width: '50px', fontSize: '11px', color: 'var(--text-secondary)' }}>{time}</td>
                        <td style={{ fontWeight: 500 }}>{e.consultantName}</td>
                        <td>
                          <span className="pill" style={{ background: display.color, color: '#fff', padding: '2px 8px', fontSize: '11px', borderRadius: '4px' }}>
                            {display.label}
                          </span>
                        </td>
                        <td style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                          {e.recordName && /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(e.recordName) ? '–' : e.recordName}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
        </>
      )}
    </div>
  );
}
